import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PlanComparisonResult, shouldShowFeature } from '../../../services/planComparisonService';
import { Button } from '../../ui/Button';
import PaymentButtons from '../PaymentButtons';

interface WelcomeStepProps {
  comparison: PlanComparisonResult;
  onNext: () => void;
}

export default function WelcomeStep({ comparison, onNext }: WelcomeStepProps) {
  const { t } = useTranslation();
  const { fromPlan, toPlan, changes } = comparison;
  const [showPayment, setShowPayment] = useState(false);
  
  // Filtrar solo features que el usuario debe ver
  const userFacingChanges = changes.filter(c => shouldShowFeature(c.feature));
  
  // Si está mostrando el panel de pago, renderizar eso
  if (showPayment) {
    return <PaymentButtons onCancel={() => setShowPayment(false)} />;
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="text-6xl mb-4">🔄</div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          {t('downgrade.planChangeDetected')}
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          {t('downgrade.subscriptionExpiredSetupFree')}
        </p>
      </div>
      
      {/* Plan comparison */}
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-xl p-6 border-2 border-purple-200 dark:border-purple-800">
        <div className="flex items-center justify-between">
          <div className="flex-1 text-center">
            <div className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
              {t('downgrade.previousPlan')}
            </div>
            <div className="text-xl font-bold text-gray-900 dark:text-white">
              {fromPlan.name}
            </div>
            <div className="text-sm text-purple-600 dark:text-purple-400 mt-1">
              ${fromPlan.priceMonthly || fromPlan.price}{t('subscription.perMonth')}
            </div>
          </div>
          
          <div className="px-4">
            <div className="text-3xl">→</div>
          </div>
          
          <div className="flex-1 text-center">
            <div className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
              {t('downgrade.newPlan')}
            </div>
            <div className="text-xl font-bold text-gray-900 dark:text-white">
              {toPlan.name}
            </div>
            <div className="text-sm text-green-600 dark:text-green-400 mt-1">
              {t('downgrade.free')}
            </div>
          </div>
        </div>
      </div>
      
      {/* Reassurance message */}
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-6 border-2 border-blue-200 dark:border-blue-800">
        <div className="flex gap-4">
          <div className="text-3xl flex-shrink-0">⏸️</div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              {t('downgrade.nothingWillBeDeleted')}
            </h3>
            <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
              <li className="flex items-start gap-2">
                <span className="text-green-500 font-bold mt-0.5">✓</span>
                <span>{t('downgrade.contentSavedSecurely')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500 font-bold mt-0.5">✓</span>
                <span>{t('downgrade.chooseWhatToKeep')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500 font-bold mt-0.5">✓</span>
                <span>{t('downgrade.notSelectedWillPause')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500 font-bold mt-0.5">✓</span>
                <span>{t('downgrade.renewToRestore')}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
      
      {/* What you need to do */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border-2 border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <span>📝</span>
          <span>{t('downgrade.whatWeWillDo')}</span>
        </h3>
        
        {/* Items que necesitan selección */}
        {userFacingChanges.filter(c => c.needsSelection).length > 0 && (
          <div className="space-y-3 mb-4">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              📝 <strong>{t('downgrade.willAskToSelect')}</strong>
            </p>
            {userFacingChanges.filter(c => c.needsSelection).map((change, index) => (
              <div key={index} className="flex items-center gap-3 py-3 bg-green-50 dark:bg-green-900/10 rounded-lg px-4">
                <span className="text-green-500 font-bold text-xl">✓</span>
                <span className="font-semibold text-gray-900 dark:text-white flex-1">
                  {change.featureName}
                </span>
                <span className="text-sm font-medium text-green-700 dark:text-green-400 bg-green-100 dark:bg-green-900/30 px-3 py-1 rounded-full">
                  {t('downgrade.choose')} {change.selectionLimit}
                </span>
              </div>
            ))}
          </div>
        )}
        
        {/* Features bloqueadas */}
        {userFacingChanges.filter(c => c.status === 'blocked' || c.status === 'limited_to_blocked').length > 0 && (
          <div className="space-y-3 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              ⏸️ <strong>{t('downgrade.theseFeaturesPaused')}</strong>
            </p>
            {userFacingChanges.filter(c => c.status === 'blocked' || c.status === 'limited_to_blocked').map((change, index) => (
              <div key={index} className="flex items-center gap-3 py-3 bg-amber-50 dark:bg-amber-900/10 rounded-lg px-4">
                <span className="text-amber-500 text-xl">⏸️</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {change.featureName}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Action buttons */}
      <div className="flex flex-col sm:flex-row gap-3">
        <Button
          onClick={onNext}
          className="flex-1 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white py-3"
        >
          {t('downgrade.continueWithFreePlan')}
        </Button>
        <Button
          onClick={() => setShowPayment(true)}
          variant="outline"
          className="flex-1 border-2 border-purple-600 text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20 py-3"
        >
          {t('downgrade.renewMyPlanNow')}
        </Button>
      </div>
      
      {/* Help text */}
      <p className="text-xs text-center text-gray-500 dark:text-gray-400">
        {t('downgrade.processWillTake')}
      </p>
    </div>
  );
}
